import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, Button, Alert, Skeleton, message, Modal } from 'antd';
import {
  InfoCircleOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  CreditCardOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { useMerchantDetail } from '../../hooks/useMerchantDetail';
import { StatusBadge } from '../../design-system';
import api from '../../services/api';
import {
  fetchMerchantKycDetail,
  skipMerchantKycPackagePayment,
  type MerchantKycDetail,
} from '../../services/merchantKycService';
import { canAdminSkipKycPackagePayment } from '../../utils/merchantKycAdmin';
import {
  MerchantDetailTabs,
  type MerchantDetailTabKey,
} from '../../components/merchant-detail/MerchantDetailTabs';
import { GeneralInfoTab } from '../../components/merchant-detail/GeneralInfoTab';
import { MenuTab } from '../../components/merchant-detail/MenuTab';
import { AnalyticsTab } from '../../components/merchant-detail/AnalyticsTab';
import { PaymentMethodsTab } from '../../components/merchant-detail/PaymentMethodsTab';
import { formatVnDate } from '../../utils/vnDateTime';
import { MerchantAvatar } from '../../components/MerchantAvatar';

function getInitials(name: string | undefined | null): string {
  if (!name || !name.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  '#047857',
  '#2563eb',
  '#7c3aed',
  '#ea580c',
  '#db2777',
  '#4b5563',
];

function avatarColor(name: string | undefined | null): string {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h << 5) - h + name.charCodeAt(i);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function statusLabel(isActive: boolean | undefined): 'active' | 'suspended' {
  return isActive === true ? 'active' : 'suspended';
}

export default function MerchantDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<MerchantDetailTabKey>('general');
  const [suspendLoading, setSuspendLoading] = useState(false);
  const [skipPackageLoading, setSkipPackageLoading] = useState(false);
  const [kycDetail, setKycDetail] = useState<MerchantKycDetail | null>(null);

  const {
    data: merchant,
    loading: merchantLoading,
    error: merchantError,
    refetch: refetchMerchant,
  } = useMerchantDetail(id);
  const restaurantId = merchant?.restaurantId;

  useEffect(() => {
    if (!merchant?.id) {
      setKycDetail(null);
      return;
    }
    if (canAdminSkipKycPackagePayment(merchant)) {
      return;
    }
    let cancelled = false;
    fetchMerchantKycDetail(merchant.id)
      .then(res => {
        if (!cancelled && res.success && res.data) {
          setKycDetail(res.data);
        }
      })
      .catch(() => {
        if (!cancelled) setKycDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [
    merchant?.id,
    merchant?.kycStatus,
    merchant?.packagePaymentStatus,
    merchant?.isKycApproved,
    merchant?.isPackagePaymentSatisfied,
    merchant?.onboarding,
  ]);

  if (merchantError) {
    return (
      <div className="merchant-detail-page">
        <header className="merchant-detail-header">
          <h1 className="merchant-detail-title">Merchant Details</h1>
          <nav className="merchant-detail-breadcrumb">
            <Link to="/">Dashboard</Link>
            <span className="merchant-detail-breadcrumb-sep">&gt;</span>
            <Link to="/merchants">Merchants</Link>
            <span className="merchant-detail-breadcrumb-sep">&gt;</span>
            <span className="merchant-detail-breadcrumb-current">–</span>
          </nav>
        </header>
        <Alert
          type="error"
          message={merchantError}
          showIcon
          closable
          className="merchant-detail-alert"
        />
      </div>
    );
  }

  if (merchantLoading && !merchant) {
    return (
      <div className="merchant-detail-page">
        <header className="merchant-detail-header">
          <h1 className="merchant-detail-title">Merchant Details</h1>
          <nav className="merchant-detail-breadcrumb">
            <Link to="/">Dashboard</Link>
            <span className="merchant-detail-breadcrumb-sep">&gt;</span>
            <Link to="/merchants">Merchants</Link>
          </nav>
        </header>
        <Card className="merchant-detail-header-card">
          <Skeleton active avatar paragraph={{ rows: 2 }} />
        </Card>
        <div className="merchant-detail-tabs">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      </div>
    );
  }

  const displayName = merchant?.restaurantName || merchant?.name || 'Merchant';
  const status = statusLabel(merchant?.is_active);
  const canSkipKycPackagePayment = canAdminSkipKycPackagePayment(
    merchant,
    kycDetail
  );

  const tabItems = [
    {
      key: 'general' as const,
      label: 'General Info',
      icon: <InfoCircleOutlined />,
      children: (
        <GeneralInfoTab
          merchant={merchant}
          merchantId={id}
          onEditClick={() => id && navigate(`/merchants/${id}/edit`)}
          onViewReportClick={() => setActiveTab('analytics')}
        />
      ),
    },
    {
      key: 'menu' as const,
      label: 'Menu',
      icon: <AppstoreOutlined />,
      children: <MenuTab merchantId={id} restaurantId={restaurantId} />,
    },
    {
      key: 'analytics' as const,
      label: 'Analytics',
      icon: <BarChartOutlined />,
      children: <AnalyticsTab merchantId={id} />,
    },
    {
      key: 'paymentMethods' as const,
      label: 'Payment method',
      icon: <CreditCardOutlined />,
      children: <PaymentMethodsTab restaurantId={restaurantId ?? undefined} />,
    },
  ];

  return (
    <div className="merchant-detail-page">
      <header className="merchant-detail-header">
        <h1 className="merchant-detail-title">Merchant Details</h1>
        <nav className="merchant-detail-breadcrumb">
          <Link to="/">Dashboard</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <Link to="/merchants">Merchants</Link>
          <span className="merchant-detail-breadcrumb-sep">&gt;</span>
          <span className="merchant-detail-breadcrumb-current">
            {displayName}
          </span>
        </nav>
      </header>

      <Card className="merchant-detail-header-card">
        <div className="merchant-detail-header-card-body">
          <div className="merchant-detail-header-card-left">
            <MerchantAvatar
              name={displayName}
              imageUrl={merchant?.restaurant_image}
              size={56}
              borderRadius={12}
              className="merchant-detail-header-logo"
            />
            <div className="merchant-detail-header-info">
              <h2 className="merchant-detail-header-name">
                {displayName}
                <StatusBadge
                  status={status}
                  type="user"
                  customText={status === 'active' ? 'Active' : 'Suspended'}
                />
              </h2>
              <div className="merchant-detail-header-meta">
                <span className="merchant-detail-header-meta-item">
                  <ShopOutlined className="merchant-detail-header-meta-icon" />
                  {merchant?.categoryName || 'Chưa phân loại'}
                </span>
                <span className="merchant-detail-header-meta-item">
                  <EnvironmentOutlined className="merchant-detail-header-meta-icon" />
                  {merchant?.restaurantAddress || '–'}
                </span>
                <span className="merchant-detail-header-meta-item">
                  <CalendarOutlined className="merchant-detail-header-meta-icon" />
                  {merchant?.created_at
                    ? `Joined ${formatVnDate(
                        merchant.created_at,
                        'MMM D, YYYY'
                      )}`
                    : '–'}
                </span>
              </div>
            </div>
          </div>
          <div className="merchant-detail-header-card-actions">
            {canSkipKycPackagePayment ? (
              <Button
                loading={skipPackageLoading}
                onClick={() => {
                  if (!id || !merchant) return;
                  Modal.confirm({
                    title: 'Bỏ qua thanh toán gói KYC',
                    content:
                      'Quán sẽ được đưa lên app khách và tài khoản merchant chuyển sang trạng thái Active mà không cần thanh toán gói đăng ký. Tiếp tục?',
                    okText: 'Bỏ qua & lên sàn',
                    okButtonProps: { danger: true },
                    onOk: async () => {
                      try {
                        setSkipPackageLoading(true);
                        const result = await skipMerchantKycPackagePayment(id);
                        if (!result.success) {
                          message.error(
                            result.message ||
                              'Không thể bỏ qua thanh toán gói KYC'
                          );
                          return;
                        }
                        await refetchMerchant();
                        setKycDetail(null);
                        message.success(
                          result.message || 'Đã đưa quán lên sàn thành công'
                        );
                      } catch (err: unknown) {
                        const ax = err as {
                          response?: { data?: { message?: string } };
                        };
                        message.error(
                          ax.response?.data?.message ||
                            'Không thể bỏ qua thanh toán gói KYC'
                        );
                        throw err;
                      } finally {
                        setSkipPackageLoading(false);
                      }
                    },
                  });
                }}
              >
                Bỏ qua thanh toán gói & lên sàn
              </Button>
            ) : null}
            <Button
              className="merchant-detail-btn-suspend"
              loading={suspendLoading}
              onClick={() => {
                if (!id || !merchant) return;
                const isSuspend = merchant.is_active === true;
                const newIsActive = isSuspend ? false : true;
                Modal.confirm({
                  title: isSuspend ? 'Suspend Merchant' : 'Activate Merchant',
                  content: isSuspend
                    ? 'Are you sure you want to suspend this merchant? They will not be able to receive orders.'
                    : 'Are you sure you want to activate this merchant?',
                  okText: isSuspend ? 'Suspend' : 'Activate',
                  okButtonProps: { danger: isSuspend },
                  onOk: async () => {
                    try {
                      setSuspendLoading(true);
                      await api.put(`/merchants/${id}`, {
                        name: merchant.name,
                        email: merchant.email,
                        phone: merchant.phone,
                        address: merchant.address,
                        restaurantName: merchant.restaurantName,
                        restaurantAddress: merchant.restaurantAddress,
                        isActive: newIsActive,
                      });
                      await refetchMerchant();
                      message.success(
                        isSuspend ? 'Merchant suspended' : 'Merchant activated'
                      );
                      navigate('/merchants?refresh=1');
                    } catch (err: unknown) {
                      const ax = err as {
                        response?: { data?: { message?: string } };
                      };
                      message.error(
                        ax.response?.data?.message || 'Thao tác thất bại'
                      );
                      throw err;
                    } finally {
                      setSuspendLoading(false);
                    }
                  },
                });
              }}
            >
              {merchant?.is_active ? 'Suspend' : 'Activate'}
            </Button>
            <Button
              type="primary"
              className="merchant-detail-btn-edit"
              onClick={() => id && navigate(`/merchants/${id}/edit`)}
            >
              Edit Profile
            </Button>
          </div>
        </div>
      </Card>

      <MerchantDetailTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </div>
  );
}
